import {
  generateMultiTonePalette,
  typographyForVibe,
  VIBE_COPY,
  VIBE_FLAVOR,
  VibeEnum,
  type Vibe,
} from '@flashpoint/site-dna';
import type { StepProps } from '../stepProps';
import { OptionCard } from '../OptionCard';

/**
 * VibeStep
 * ---------------------------------------------------------------
 * Picks the color + typography flavor inside the Nuzum Method signature.
 * Swatches come from VIBE_FLAVOR (the structured source of truth that
 * stays in lockstep with master-scaffold/src/styles/signature.css).
 *
 * Read-aloud copy comes from VIBE_COPY for use during discovery calls.
 */
export function VibeStep({ lead, queueField }: StepProps) {
  const current = lead.siteDna.designTokens.vibe;

  const select = (vibe: Vibe) => {
    queueField('siteDna.designTokens.vibe', vibe);
    queueField('siteDna.designTokens.multiTonePalette', generateMultiTonePalette(vibe));
    queueField('siteDna.designTokens.typography', typographyForVibe(vibe));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-nm-muted">
        Vibe controls the color + typography flavor inside the Nuzum Method signature. Picking a
        vibe regenerates the palette and font stacks. Layout structure happens on the next step.
        Read the pitch on any card aloud to clients on a discovery call.
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        {VibeEnum.options.map((opt) => {
          const v = opt as Vibe;
          const flavor = VIBE_FLAVOR[v];
          const copy = VIBE_COPY[v];
          const selected = current === v;
          return (
            <OptionCard
              key={opt}
              copy={copy}
              selected={selected}
              onSelect={() => select(v)}
              ariaId={`vibe-${v.replace(/\s+/g, '-').toLowerCase()}`}
              extraBody={
                <div className="space-y-2">
                  <div
                    className="flex items-center gap-2 rounded-lg p-2"
                    style={{ background: '#121410' }}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-widest text-nm-muted">
                      Leaks
                    </span>
                    <div className="flex gap-1">
                      {flavor.leaks.map((c) => (
                        <span
                          key={c}
                          className="inline-block h-5 w-5 rounded-full border border-black/30 shadow-sm"
                          style={{ background: c }}
                          title={c}
                        />
                      ))}
                    </div>
                    <span className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-nm-muted">
                      Accent
                      <span
                        className="inline-block h-5 w-5 rounded-full border border-black/30"
                        style={{ background: flavor.accent }}
                        title={flavor.accent}
                      />
                    </span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-nm-muted">
                    feel · {flavor.feelHint}
                  </div>
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}
